/**
 * Copyright IBM Corp. 2022, 2026
 */

import { vercelSplit } from '@web/cms'
import {
	BUTTON_COLORS,
	BUTTON_SIZES,
	BUTTON_ICON_POSITIONS,
} from '@hashicorp/mds-react/components'
import type {
	ButtonProps,
	ButtonColor,
	ButtonIconPosition,
	ButtonSize,
} from '@hashicorp/mds-react/components'
import {
	hasHashiCorpDestination,
	parseUrl,
} from '@hashicorp/react-components/src/lib/parse-url'
import type { ButtonV2FieldsFragment } from 'types/dato'

/**
 * Format buttonV2Fields fragment from DatoCMS to props for @hashicorp/mds-react <Button /> component
 */
export default function formatButtonToProps({
	href,
	color,
	size,
	iconPosition,
	icon,
	...rest
}: ButtonV2FieldsFragment): ButtonProps {
	const parsedUrl = parseUrl(vercelSplit(href))
	return {
		...rest,
		href: parsedUrl.href,
		color: isValidColor(color) ? vercelSplit(color) : undefined,
		size: isValidSize(size) ? vercelSplit(size) : undefined,
		iconPosition: isValidIconPosition(iconPosition)
			? vercelSplit(iconPosition)
			: undefined,
		icon: icon && icon.length ? vercelSplit(icon) : undefined,
		isHrefExternal: !hasHashiCorpDestination(parsedUrl.href),
	}
}

function isValidColor(colorVal?: string): colorVal is ButtonColor {
	return (
		!!colorVal && BUTTON_COLORS.includes(vercelSplit(colorVal) as ButtonColor)
	)
}

function isValidSize(sizeVal?: string): sizeVal is ButtonSize {
	return !!sizeVal && BUTTON_SIZES.includes(vercelSplit(sizeVal) as ButtonSize)
}

function isValidIconPosition(
	iconPositionVal?: string,
): iconPositionVal is ButtonIconPosition {
	return (
		!!iconPositionVal &&
		BUTTON_ICON_POSITIONS.includes(
			vercelSplit(iconPositionVal) as ButtonIconPosition,
		)
	)
}
